import { computed, observable } from 'mobx';
import ReserveInfo, {
  convertReserveInfoResponseToArgs,
  type PaymentGatewayMember,
  type ShinkansenAddressType
} from './reserve_info2';
import ApprovalAuthority from './approval_authority/approval_authority';
import Approver from './approver/approver';
import Department from './department/department2';
import User, { convertUserResponseToArgs, type UserResponseArgs } from './user/user2';
import type { SelectType } from './select_store';
import type { ReservingTripItemResponseArgs } from './trip/reserving_trip_item2';
import type { TravelerInformationResponseArgs } from './traveler_information';
import { resolveTravelerFlightTel } from './traveler_information';
import type { RentalCarResponseArgs } from './rental_car';
import type { PaymentGatewayType } from './organization/organization3';
import type { BakurakuWorkflowRequirementMode } from './organization/organization2';
import type { SearchType } from './search_query2';
import type { TripRuleErrorArgs } from './trip/trip_rule_error';
import TripRuleError from './trip/trip_rule_error';
import type { TripRuleDistanceTimeInfoArgs } from './trip/trip_rule_distance_time_info';
import TripRuleDistanceTimeInfo from './trip/trip_rule_distance_time_info';
import type { TripDuplicationResponseArgs } from './trip_duplication';
import type { ExicCardResponseArgs } from './exic_card2';
import ExicCard, { convertExicCardResponseToArgs } from './exic_card2';
import type { DepartmentResponseArgs } from './department/department2';
import type { OrganizationBaseResponseArgs } from './organization_base/organization_base2';
import OrganizationBase, { convertOrganizationBaseResponseToArgs } from './organization_base/organization_base2';
import type { ChargingDepartmentShareResponseArgs } from './department/charging_department_share2';
import type { ExpensesAccountTypeResponseArgs } from './expenses/expenses_account_type2';
import type { ProjectResponseArgs } from './project/project2';
import type { ProjectShareResponseArgs } from './project/project_share2';
import type { ConsignmentSaleResponseArgs } from './consignment_sale';
import type { MarginTypeResponseArgs } from './organization/margin_type3';
import type { ApproverArgs } from './approver/approver';
import type { WorkflowStyle } from './workflow_style';
import type { RentalCarLimitTypeJson } from './organization/rental_car_limit_type';
import type { StationJson, RailwayAreaJson } from './station';

export type ReserveConfirmStoreResponseArgs = {
  user: UserResponseArgs;
  type?: SelectType;
  items: ReservingTripItemResponseArgs[];
  travelers: TravelerInformationResponseArgs[];
  traveler_candidates?: UserResponseArgs[];
  shinkansen_address_type?: ShinkansenAddressType;
  shinkansen_address?: string;
  shinkansen_addressee?: string;
  postcode?: string;
  all_travelers_notification?: boolean;
  package_type?: string;
  package_search_condition?: {
    select_page_url?: string;
  };
  rental_cars?: RentalCarResponseArgs[];
  foreign_exist?: boolean;
  payment_gateway_type?: PaymentGatewayType;
  payment_gateway_members?: PaymentGatewayMember[];
  search_query_id?: number;
  token_host?: string;
  shop_id?: string;
  sony_payment_gateway_token_generate_script_url?: string;
  select_page_url?: string;
  queries?: { search_type: SearchType; peoplenum?: number | string; roomnum?: number | string };
  trip_rule_errors?: TripRuleErrorArgs[];
  trip_distance_and_time_info?: TripRuleDistanceTimeInfoArgs[];
  trip_duplications?: TripDuplicationResponseArgs[];
  has_members?: boolean;
  login_user_id?: number;
  nationalities: { id: number; name: string }[];
  stations?: StationJson[];
  railway_areas?: RailwayAreaJson[];
  show_ex: boolean;
  show_business_ekinet?: boolean;
  show_fee?: boolean;
  use_kyuusyuu_ticket: boolean;
  use_bulk_ticket: boolean;
  exic_cards: ExicCardResponseArgs[];
  departments: DepartmentResponseArgs[];
  charging_department_shares: ChargingDepartmentShareResponseArgs[];
  organization_bases: OrganizationBaseResponseArgs[];
  rentalcar_available?: boolean;
  rental_car_limit_type?: RentalCarLimitTypeJson;
  car_type: [string, string][];
  use_default_workflow?: boolean;
  use_charging_department_workflow: boolean;
  workflow_style?: WorkflowStyle;
  concur_expense_enabled?: boolean;
  concur_connected?: boolean;
  tabiregi_consent?: {
    consented: boolean;
    registered_at: string | null;
  };
  expenses_account_types: ExpensesAccountTypeResponseArgs[];
  project_share_availability?: boolean;
  projects: ProjectResponseArgs[];
  project_shares: ProjectShareResponseArgs[];
  package_provider: string;
  consignment_sale: ConsignmentSaleResponseArgs;
  margin_types: MarginTypeResponseArgs[];
  airline?: 'ANA' | 'JAL';
  approvers?: ApproverArgs[];
  expenses_account_type_available?: boolean;
  bakuraku_workflow_integration_enabled?: boolean;
  bakuraku_workflow_requirement_mode?: BakurakuWorkflowRequirementMode | null;
  draft_trip?: {
    purpose?: string | null;
    bakuraku_workflow_request_id?: string | null;
    bakuraku_workflow_request_title?: string | null;
    bakuraku_workflow_request_num?: string | null;
  } | null;
};

export type ReserveConfirmStoreArgs = {
  reserveInfo: ReserveInfo;
  tokenHost?: string;
  shopId?: string;
  sonyPaymentGatewayTokenGenerateScriptUrl?: string;
  selectPageUrl?: string;
  queries?: { searchType: SearchType };
  tripRuleErrors?: TripRuleError[];
  tripDistanceAndTimeInfo?: TripRuleDistanceTimeInfo[];
  showEx?: boolean;
  showBusinessEkinet?: boolean;
  useKyuusyuuTicket?: boolean;
  useBulkTicket?: boolean;
  exicCards?: ExicCard[];
  organizationBases?: OrganizationBase[];
  fromTripForm: boolean;
  // 申請から始めて Smart Search（検索結果・レンタカータブ）を使った場合 true
  fromSmartSearch?: boolean;
  stations?: StationJson[];
  railwayAreas?: RailwayAreaJson[];
};

type UserResponseWithGradeId = UserResponseArgs & { grade_id?: string | number | null };
type UserResponseWithApproveStage = UserResponseArgs & { approve_stage?: number | null };
type UserWithApproveStage = User & { approve_stage?: number | null };

const buildApprovalAuthoritiesFromApprovers = (approvers?: UserResponseWithApproveStage[]) => {
  if (!approvers || approvers.length === 0) {
    return undefined;
  }

  return [...approvers]
    .sort((a, b) => (a.approve_stage ?? 0) - (b.approve_stage ?? 0))
    .map(approver => {
      const approveStage = approver.approve_stage ?? 0;
      return new ApprovalAuthority({
        approveStage,
        approverId: approver.id,
        approver: new Approver({
          approve_stage: approveStage,
          name: approver.name
        })
      });
    });
};

export const convertReserveConfirmStoreResponseToArgs = (
  response: ReserveConfirmStoreResponseArgs,
  options: { isSimpleRequest: boolean; fromTripForm: boolean; fromSmartSearch?: boolean }
) => {
  const reserveInfo = new ReserveInfo(
    convertReserveInfoResponseToArgs({
      user: response.user,
      type: response.type,
      items: response.items,
      travelers: response.travelers,
      shinkansen_address_type: response.shinkansen_address_type,
      shinkansen_address: response.shinkansen_address,
      shinkansen_addressee: response.shinkansen_addressee,
      postcode: response.postcode,
      all_travelers_notification: response.all_travelers_notification,
      package_type: response.package_type,
      package_search_condition: response.package_search_condition,
      roomnum: response.queries?.roomnum,
      rental_cars: response.rental_cars,
      foreign_exist: response.foreign_exist,
      payment_gateway_type: response.payment_gateway_type,
      payment_gateway_members: response.payment_gateway_members,
      has_members: response.has_members,
      is_simple_request: options.isSimpleRequest,
      login_user_id: response.login_user_id,
      nationalities: response.nationalities,
      rentalcar_available: response.rentalcar_available,
      rental_car_limit_type: response.rental_car_limit_type,
      car_type: response.car_type,
      use_default_workflow: response.use_default_workflow,
      use_charging_department_workflow: response.use_charging_department_workflow,
      workflow_style: response.workflow_style,
      concur_expense_enabled: response.concur_expense_enabled,
      concur_connected: response.concur_connected,
      tabiregi_consent: response.tabiregi_consent,
      departments: response.departments,
      charging_department_shares: response.charging_department_shares,
      expenses_account_types: response.expenses_account_types,
      project_share_availability: response.project_share_availability,
      projects: response.projects,
      project_shares: response.project_shares,
      package_provider: response.package_provider,
      consignment_sale: response.consignment_sale,
      trip_duplications: response.trip_duplications,
      margin_types: response.margin_types,
      airline: response.airline,
      approvers: response.approvers,
      expenses_account_type_available: response.expenses_account_type_available,
      bakuraku_workflow_integration_enabled: response.bakuraku_workflow_integration_enabled,
      bakuraku_workflow_requirement_mode: response.bakuraku_workflow_requirement_mode,
      show_fee: response.show_fee,
      search_query_id: response.search_query_id
    })
  );

  const members =
    response.traveler_candidates?.map(raw => {
      const member = new User(convertUserResponseToArgs(raw));
      const approvalAuthorities = buildApprovalAuthoritiesFromApprovers(
        raw.department?.approvers as UserResponseWithApproveStage[] | undefined
      );
      if (member.department && approvalAuthorities) {
        const approvers = (raw.department?.approvers ?? []) as UserResponseWithApproveStage[];
        member.department.approvalAuthorities = approvalAuthorities;
        member.department.approvers = approvers.map(
          approver =>
            Object.assign(new User(convertUserResponseToArgs(approver)), {
              approve_stage: approver.approve_stage
            }) as UserWithApproveStage
        );
      }
      return member;
    }) ?? [];
  const departmentMap = new Map(reserveInfo.departments.map(department => [department.id, department]));
  const memberDepartmentIdMap = new Map(
    response.traveler_candidates?.map(raw => [raw.id, raw.department_id]) ?? []
  );
  const userGradeIds = [response.user, ...(response.traveler_candidates ?? [])]
    .map(raw => {
      const gradeId = (raw as UserResponseWithGradeId).grade_id;
      return gradeId ? ([raw.id, Number(gradeId)] as const) : undefined;
    })
    .filter((entry): entry is readonly [number, number] => entry !== undefined);
  const memberGradeIdMap = new Map(userGradeIds);
  const memberMap = new Map(members.map(member => [member.id, member]));
  reserveInfo.reservingTrip.travelers.forEach(traveler => {
    const existingTravelerUser = traveler.user;
    const loginUserMatch =
      traveler.userId === reserveInfo.loginUserId || traveler.user?.id === reserveInfo.loginUserId;
    const member = loginUserMatch
      ? reserveInfo.user
      : (traveler.userId != null ? memberMap.get(traveler.userId) : undefined) ||
        (traveler.user?.id != null ? memberMap.get(traveler.user.id) : undefined);
    if (member) {
      if (!member.grade && existingTravelerUser?.grade) {
        member.grade = existingTravelerUser.grade;
      }
      if (!member.grade) {
        const gradeId = memberGradeIdMap.get(member.id);
        member.grade = reserveInfo.user.organization?.grades.find(grade => grade.id === gradeId);
      }
      if (!member.department?.name) {
        const departmentId = memberDepartmentIdMap.get(member.id);
        if (departmentId) {
          const fullDepartment = departmentMap.get(departmentId);
          if (fullDepartment) {
            member.department = new Department({
              id: fullDepartment.id,
              code: fullDepartment.code,
              name: fullDepartment.name,
              approvalAuthorities: member.department?.approvalAuthorities ?? fullDepartment.approvalAuthorities,
              approvers: member.department?.approvers ?? fullDepartment.approvers,
              organizationAddress: fullDepartment.organizationAddress
            });
          }
        }
      }
      traveler.user = member;
      if (!traveler.name) {
        traveler.name = member.name;
      }
      if (!traveler.userName) {
        traveler.userName = member.name;
      }
      if (!traveler.birthday) {
        traveler.birthday = member.birthday;
      }
      if (!traveler.lastNameKana) {
        traveler.lastNameKana = member.lastNameKana;
      }
      if (!traveler.firstNameKana) {
        traveler.firstNameKana = member.firstNameKana;
      }
      if (!traveler.lastNameRoman) {
        traveler.lastNameRoman = member.lastNameRoman;
      }
      if (!traveler.firstNameRoman) {
        traveler.firstNameRoman = member.firstNameRoman;
      }
      if (loginUserMatch) {
        traveler.type = 'self';
        traveler.userId = member.id;
        traveler.userName = member.name;
        traveler.birthday = member.birthday;
        traveler.lastNameKana = member.lastNameKana;
        traveler.firstNameKana = member.firstNameKana;
        traveler.lastNameRoman = member.lastNameRoman;
        traveler.firstNameRoman = member.firstNameRoman;
        traveler.flightLastName = member.lastName;
        traveler.flightFirstName = member.firstName;
        traveler.flightMiddleName = '';
        traveler.flightLastNameKana = member.lastNameKana;
        traveler.flightFirstNameKana = member.firstNameKana;
        traveler.flightLastNameRoman = member.lastNameRoman;
        traveler.flightFirstNameRoman = member.firstNameRoman;
        traveler.flightMiddleNameRoman = '';
        traveler.flightBirthday = member.birthday;
        traveler.flightGender = member.gender as typeof traveler.flightGender;
        traveler.passportNumber = member.passportNumber;
        traveler.passportExpire = member.passportExpire;
      }
      if ((!traveler.mileageNumbers || traveler.mileageNumbers.length === 0) && member.mileageNumbers?.length) {
        traveler.mileageNumbers = member.mileageNumbers;
      }
      if (reserveInfo.user.organization?.exicUsageMode === 'card_based' && member.exicCardId != null) {
        traveler.exicCardId = member.exicCardId;
      } else if (
        (traveler.exicCardId == null || String(traveler.exicCardId) === '') &&
        member.exicCardId != null
      ) {
        traveler.exicCardId = member.exicCardId;
      }
    }
    if (!traveler.type) {
      if (member) {
        traveler.type = member.id === reserveInfo.loginUserId ? 'self' : 'member';
      } else if (traveler.userId) {
        traveler.type = 'member';
      } else {
        traveler.type = 'companion';
      }
    }
    const fallbackFlightLastName = member?.lastName || traveler.lastNameKana;
    const fallbackFlightFirstName = member?.firstName || traveler.firstNameKana;
    const fallbackFlightLastNameKana = member?.lastNameKana || traveler.lastNameKana;
    const fallbackFlightFirstNameKana = member?.firstNameKana || traveler.firstNameKana;
    const fallbackFlightLastNameRoman = member?.lastNameRoman || traveler.lastNameRoman;
    const fallbackFlightFirstNameRoman = member?.firstNameRoman || traveler.firstNameRoman;
    const fallbackFlightBirthday = member?.birthday || traveler.birthday;
    const fallbackFlightGender =
      (member?.gender as typeof traveler.flightGender | undefined) || traveler.flightGender;
    if (!traveler.flightLastName) {
      traveler.flightLastName = fallbackFlightLastName;
    }
    if (!traveler.flightFirstName) {
      traveler.flightFirstName = fallbackFlightFirstName;
    }
    if (!traveler.flightLastNameKana) {
      traveler.flightLastNameKana = fallbackFlightLastNameKana;
    }
    if (!traveler.flightFirstNameKana) {
      traveler.flightFirstNameKana = fallbackFlightFirstNameKana;
    }
    if (!traveler.flightLastNameRoman) {
      traveler.flightLastNameRoman = fallbackFlightLastNameRoman;
    }
    if (!traveler.flightFirstNameRoman) {
      traveler.flightFirstNameRoman = fallbackFlightFirstNameRoman;
    }
    if (!traveler.flightBirthday) {
      traveler.flightBirthday = fallbackFlightBirthday;
    }
    if (!traveler.flightGender) {
      traveler.flightGender = fallbackFlightGender;
    }
    traveler.flightTel = resolveTravelerFlightTel({
      type: traveler.type,
      currentFlightTel: traveler.flightTel,
      user: member
    });
  });
  reserveInfo.syncSharedShinkansenSeatFromTravelerGrades();

  const args: ReserveConfirmStoreArgs = {
    reserveInfo,
    tokenHost: response.token_host,
    shopId: response.shop_id,
    sonyPaymentGatewayTokenGenerateScriptUrl: response.sony_payment_gateway_token_generate_script_url,
    selectPageUrl: response.select_page_url,
    queries: response.queries ? { searchType: response.queries.search_type } : undefined,
    tripRuleErrors: response.trip_rule_errors
      ? response.trip_rule_errors.map(raw => new TripRuleError(raw))
      : undefined,
    tripDistanceAndTimeInfo: response.trip_distance_and_time_info
      ? response.trip_distance_and_time_info.map(raw => new TripRuleDistanceTimeInfo(raw))
      : undefined,
    showEx: response.show_ex,
    showBusinessEkinet: response.show_business_ekinet ?? false,
    useKyuusyuuTicket: response.use_kyuusyuu_ticket,
    useBulkTicket: response.use_bulk_ticket,
    exicCards: response.exic_cards
      ? response.exic_cards.map(raw => new ExicCard(convertExicCardResponseToArgs(raw)))
      : undefined,
    organizationBases: response.organization_bases
      ? response.organization_bases.map(raw => new OrganizationBase(convertOrganizationBaseResponseToArgs(raw)))
      : undefined,
    fromTripForm: options.fromTripForm ?? false,
    fromSmartSearch: options.fromSmartSearch ?? false,
    stations: response.stations || [],
    railwayAreas: response.railway_areas || []
  };
  return args;
};

export default class ReserveConfirmStore {
  @observable reserveInfo: ReserveInfo;
  @observable tokenHost?: string;
  @observable shopId?: string;
  @observable sonyPaymentGatewayTokenGenerateScriptUrl?: string;
  @observable selectPageUrl?: string;
  @observable loading: boolean;
  @observable itemsBlankError: boolean;
  @observable responseError?: string;
  @observable returning: boolean;
  @observable showError: boolean;
  @observable stepIndex: number;
  @observable queries?: { searchType: SearchType };
  @observable tripRuleErrors: TripRuleError[];
  @observable tripDistanceAndTimeInfo: TripRuleDistanceTimeInfo[];
  @observable showEx?: boolean;
  @observable showBusinessEkinet?: boolean;
  @observable useKyuusyuuTicket?: boolean;
  @observable useBulkTicket?: boolean;
  @observable exicCards: ExicCard[];
  @observable organizationBases: OrganizationBase[];
  @observable fromTripForm: boolean;
  @observable fromSmartSearch: boolean;
  @observable paymentLoading: boolean;
  @observable paymentFailed: boolean;
  @observable paymentErrors: string;
  @observable stations: StationJson[];
  @observable railwayAreas: RailwayAreaJson[];

  constructor(args: ReserveConfirmStoreArgs) {
    this.reserveInfo = args.reserveInfo;
    this.tokenHost = args.tokenHost;
    this.shopId = args.shopId;
    this.sonyPaymentGatewayTokenGenerateScriptUrl = args.sonyPaymentGatewayTokenGenerateScriptUrl;
    this.selectPageUrl = args.selectPageUrl;
    this.loading = false;
    this.itemsBlankError = false;
    this.responseError = undefined;
    this.returning = false;
    this.showError = false;
    this.stepIndex = 0;
    this.queries = args.queries;
    this.tripRuleErrors = args.tripRuleErrors ?? [];
    this.tripDistanceAndTimeInfo = args.tripDistanceAndTimeInfo ?? [];
    this.showEx = args.showEx;
    this.showBusinessEkinet = args.showBusinessEkinet;
    this.useKyuusyuuTicket = args.useKyuusyuuTicket;
    this.useBulkTicket = args.useBulkTicket;
    this.exicCards = args.exicCards ?? [];
    this.organizationBases = args.organizationBases ?? [];
    this.fromTripForm = args.fromTripForm;
    this.fromSmartSearch = args.fromSmartSearch ?? false;
    this.paymentLoading = false;
    this.paymentFailed = false;
    this.paymentErrors = '';
    this.stations = args.stations ?? [];
    this.railwayAreas = args.railwayAreas ?? [];
  }

  @computed get isCustomerInfoRequired(): boolean {
    return !!this.reserveInfo.user && !!this.reserveInfo.user.organization;
  }

  @computed get shouldShowSearchLinkingErrorKnowledgeLink(): boolean {
    const errorText = '検索条件の紐付けに失敗いたしました';

    return this.paymentErrors.includes(errorText);
  }
}
